import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Loader2, Search } from "lucide-react";
import { IntegrationLogo } from "@/components/integrations/logo";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { authLabel, integrationsApi, type IntegrationCatalogRow } from "@/lib/integrations-api";
import { cn } from "@/lib/utils";

function uniqueCategories(rows: IntegrationCatalogRow[]): string[] {
  return [...new Set(rows.map((r) => r.category))].sort();
}

function prettyCategory(category: string): string {
  return category
    .split("-")
    .map((p) => p[0]?.toUpperCase() + p.slice(1))
    .join(" ");
}

export function IntegrationsPage() {
  const { data: integrations, isPending, error } = useQuery({
    queryKey: ["integrations"],
    queryFn: integrationsApi.list,
    staleTime: 60_000,
  });

  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string | null>(null);

  const categories = useMemo(
    () => (integrations ? uniqueCategories(integrations) : []),
    [integrations],
  );

  const filtered = useMemo(() => {
    if (!integrations) return [];
    const q = query.trim().toLowerCase();
    return integrations.filter((t) => {
      if (category && t.category !== category) return false;
      if (!q) return true;
      return (
        t.name.toLowerCase().includes(q) ||
        t.slug.includes(q) ||
        t.description.toLowerCase().includes(q)
      );
    });
  }, [integrations, query, category]);

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Integrations</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Connect a integration once. Reuse its tools across every loadout in
          every project.
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search integrations…"
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => setCategory(null)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs transition-colors",
              category === null
                ? "border-foreground bg-foreground text-background"
                : "border-border text-muted-foreground hover:text-foreground",
            )}
          >
            All
          </button>
          {categories.map((c) => (
            <button
              type="button"
              key={c}
              onClick={() => setCategory(c)}
              className={cn(
                "rounded-full border px-3 py-1 text-xs transition-colors",
                category === c
                  ? "border-foreground bg-foreground text-background"
                  : "border-border text-muted-foreground hover:text-foreground",
              )}
            >
              {prettyCategory(c)}
            </button>
          ))}
        </div>
      </div>

      {isPending ? (
        <div className="flex h-40 items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
          {(error as Error).message}
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((t) => (
            <li key={t.slug}>
              <Link
                to={`/console/integrations/${t.slug}`}
                className="group flex h-full flex-col gap-3 rounded-xl border bg-card p-4 transition-colors hover:border-foreground/30 hover:bg-muted/20"
              >
                <div className="flex items-center gap-3">
                  <IntegrationLogo
                    slug={t.slug}
                    src={t.logo}
                    name={t.name}
                    size={36}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">
                      {t.name}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {prettyCategory(t.category)}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-1">
                    {t.auth.map((a, i) => (
                      <Badge
                        key={`${a.kind}-${i}`}
                        variant="outline"
                        className="font-normal"
                      >
                        {authLabel(a.kind)}
                      </Badge>
                    ))}
                  </div>
                </div>
                <p className="line-clamp-2 text-sm text-muted-foreground">
                  {t.description}
                </p>
                <div className="mt-auto flex items-center justify-between pt-2 text-xs text-muted-foreground">
                  <span>
                    {t.tools.length} tool{t.tools.length === 1 ? "" : "s"}
                    {t.events.length > 0 && (
                      <> · {t.events.length} event{t.events.length === 1 ? "" : "s"}</>
                    )}
                  </span>
                  <span className="opacity-0 transition-opacity group-hover:opacity-100">
                    View →
                  </span>
                </div>
              </Link>
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="col-span-full rounded-xl border border-dashed bg-muted/20 p-12 text-center text-sm text-muted-foreground">
              No integrations match those filters.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
